/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useDispatch, useSelector } from 'react-redux';
import SociollaText from '@components/sociolla/SociollaText';
import Input from '@components/form/Input';

import { ButtonSticky } from '@components/screen/Layout';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { logEvent } from '@global/analytics';
import Colors from '@global/colors';

const TodoNotes = ({
  navigation = null,
  containerHeight = 0,
  detailData = {},
  detailTodo = {},
  localPayload = null,
  onSubmit = () => {},
  onSaveDraft = () => {},
  resetTab = () => {},
  openCatalog = () => {},
}) => {
  const dispatch = useDispatch();
  const isLoadingGlobal = useSelector((state) => state.modal.isLoading);
  const [payloadNPD, setPayloadNPD] = useState('');
  const [payloadPlan, setPayloadPlan] = useState('');
  const MAX_NOTE_LENGTH = 500;

  const onSubmitNotes = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const payload = {
        more_info: {
          notes: [
            {
              type: 'npd_presentation',
              text: payloadNPD,
            },
          ],
          plan_for_next_visit: [
            {
              type: 'npd_presentation',
              text: payloadPlan,
            },
          ],
        },
      };
      logEvent({
        eventName: 'submit_activity',
        data: {
          activity_id: detailData?._id,
          task_id: detailTodo?._id,
          type: 'npd_presentation',
          payload: JSON.stringify(payload),
        },
        dataKey: 'detail',
      });
      await onSubmit({
        targetType: 'npd_presentation',
        taskId: detailTodo?._id,
        payload,
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      resetTab();
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      let error_message = 'A system error occurred';
      if (error?.response?.data?.message) {
        error_message = error.response.data.message;
      }
      if (!detailTodo?._id) {
        error_message = 'This activity have no todo list activity.';
      }
      dispatch(
        showAlert({
          show: true,
          isModal: true,
          title: 'Error',
          description: error_message,
          isConfirm: false,
          testID: 'todo-notes-validation',
        }),
      );
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      const source = localPayload || detailTodo?.more_info || null;
      const notes = source?.notes || source?.more_info?.notes || [];
      const plans =
        source?.plan_for_next_visit ||
        source?.more_info?.plan_for_next_visit ||
        [];
      if (notes?.length) {
        const npd = notes.find((a) => a.type == 'npd_presentation');
        if (npd) {
          const payload = npd?.text || '';
          setPayloadNPD(payload);
        }
      }
      if (plans?.length) {
        const plan_npd = plans.find((a) => a.type == 'npd_presentation');
        if (plan_npd) {
          const payload = plan_npd?.text || '';
          setPayloadPlan(payload);
        }
      }
    }
    return () => {
      mounted = false;
    };
  }, [detailTodo, localPayload]);

  const saveDraftRef = React.useRef(onSaveDraft);

  useEffect(() => {
    saveDraftRef.current = onSaveDraft;
  }, [onSaveDraft]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const payload = {
        more_info: {
          notes: [
            {
              type: 'npd_presentation',
              text: payloadNPD,
            },
          ],
          plan_for_next_visit: [
            {
              type: 'npd_presentation',
              text: payloadPlan,
            },
          ],
        },
      };
      saveDraftRef.current({
        targetType: 'npd_presentation',
        taskId: detailTodo?._id,
        payload,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [payloadNPD, payloadPlan, detailTodo?._id]);

  const goToCatalog = () => {
    if (navigation) {
      openCatalog();
    }
  };

  return (
    <KeyboardAwareScrollView
      testID="todo-notes-container"
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      enableOnAndroid={true}
      extraScrollHeight={80}>
      <View style={styles.introCard}>
        <SociollaText type="bold" style={styles.introTitle}>
          Visit Report
        </SociollaText>
        <SociollaText style={styles.introText}>
          Submit notes after your presentation to the customer.
        </SociollaText>
      </View>

      <View style={styles.sectionCard}>
        <View style={styles.sectionHeader}>
          <SociollaText type="bold" style={styles.sectionTitle}>
            Visit Report
          </SociollaText>
          <SociollaText style={styles.counterText}>
            {payloadNPD.length}/{MAX_NOTE_LENGTH}
          </SociollaText>
        </View>
        <Input
          label=""
          value={payloadNPD}
          multiline
          numberOfLines={5}
          scrollEnabled
          style={styles.inputText}
          containerStyle={styles.inputContainer}
          onChangeText={(val) =>
            setPayloadNPD((val || '').slice(0, MAX_NOTE_LENGTH))
          }
        />
      </View>

      <View style={[styles.sectionCard, styles.lastSection]}>
        <View style={styles.sectionHeader}>
          <SociollaText type="medium" style={styles.sectionTitle}>
            Plan for Next Visit
          </SociollaText>
          <SociollaText style={styles.counterText}>
            {payloadPlan.length}/{MAX_NOTE_LENGTH}
          </SociollaText>
        </View>
        <Input
          label=""
          value={payloadPlan}
          multiline
          numberOfLines={5}
          scrollEnabled
          style={styles.inputText}
          containerStyle={styles.inputContainer}
          onChangeText={(val) =>
            setPayloadPlan((val || '').slice(0, MAX_NOTE_LENGTH))
          }
        />
      </View>

      <ButtonSticky
        show
        customHost="modal-sticky-button"
        buttons={[
          {
            title: 'Catalog',
            type: 'default',
            onPress: goToCatalog,
          },
          {
            title: 'Submit Visit Report',
            disabled:
              payloadNPD && payloadPlan && !isLoadingGlobal ? false : true,
            onPress: onSubmitNotes,
          },
        ]}
      />
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: Colors.grey16,
  },
  contentContainer: {
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 220,
  },
  introCard: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.white,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 12,
  },
  introTitle: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 5,
  },
  introText: {
    fontSize: 13,
    lineHeight: 20,
    color: Colors.grey2,
  },
  sectionCard: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.white,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginBottom: 12,
  },
  lastSection: {
    marginBottom: 0,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 13,
    color: Colors.grey2,
  },
  counterText: {
    fontSize: 11,
    color: Colors.grey1,
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  inputContainer: {
    height: 122,
    borderRadius: 8,
  },
  inputText: {
    height: 102,
    textAlignVertical: 'top',
    fontSize: 13,
    color: Colors.grey2,
    paddingTop: 8,
  },
});

export default React.memo(TodoNotes, (prev, next) => prev == next);
